import React, { useState } from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import EditIcon from '@mui/icons-material/Edit'
import { BoldBodyText, BoldFormTitle, BoldSmallBodyText } from '../styles/Text'
import { Divider, FlexColumn, LightBGContainer, SpaceBetweenRowContainer } from '../styles/Containers'
import { ErrorMessage, WarningMessage } from '../common/UserMessage'
import { SecondaryButton } from '../common/Button'
import DetailSection from '../common/DetailSection'
import PIRAuthorization from './PIRAuthorization'
import EditPirDetailsDialog from './EditPirDetailsDialog'
import pirTerminology from '../../util/pirTerminology'

const EDITABLE_PIR_REQUEST_STATUSES = ['PAID', 'SIGNING', 'READY']

const SectionContainer = styled(LightBGContainer)`
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 15px;
`

const List = styled.ul`
  margin: 5px 0 0;
  padding-left: 25px;
`

const TierOptionItem = styled.div`
  font-size: 12px;
  text-wrap: wrap;
  word-break: break-word;
  margin-bottom: 0;
  line-height: 1.5;
`

const TierDetailsRow = styled(SpaceBetweenRowContainer)`
  align-items: flex-start;
  gap: 5px 15px;
`

const StyledFlexColumn = styled(FlexColumn)`
  gap: 15px;
`

const TierLabel = styled(BoldSmallBodyText)`
  width: 40%;
`

const TierValueContainer = styled.div`
  width: 60%;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const TierName = styled.span`
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
`

const TierOptionsContainer = styled(StyledFlexColumn)`
  gap: 2px;
`

const AdditionalDetailsContainer = styled(StyledFlexColumn)`
  margin-top: 15px;
  gap: 10px;
`

const renderOwnerWarning = ownerDetails => {
  if (ownerDetails?.error) {
    return <ErrorMessage>There was an error verifying if the owner matches the owner on record. Please refresh the page to try again.</ErrorMessage>
  }

  const { listedOwners = [] } = ownerDetails
  const formattedOwners = listedOwners.map(o => o.replace(/\b\w/g, char => char.toUpperCase()))

  return (
    <WarningMessage>
      {formattedOwners.length ? (
        <div>
          The entered owner did not match the owner on record. Owners on record include:
          <List>
            {formattedOwners.map((owner, index) => (
              <li key={index}>{owner}</li>
            ))}
          </List>
        </div>
      ) : (
        <div>No owners are listed for this property on record.</div>
      )}
    </WarningMessage>
  )
}

function isEmptyObject(obj = {}) {
  return Object.keys(obj).length === 0
}

function renderDetails(section, details, signature, onSignatureUploadSuccess, ownerDetails) {
  const keysWithSubchildren = Object.keys(details).filter(d => typeof details[d] === 'object')
  const detailsWithoutSubchildren = Object.keys(details).reduce((acc, d) => {
    if (typeof details[d] !== 'object') acc[d] = details[d]
    return acc
  }, {})

  return (
    <SectionContainer>
      <BoldBodyText>{section}</BoldBodyText>
      <Divider />
      {!isEmptyObject(ownerDetails) && !ownerDetails.ownerMatches && renderOwnerWarning(ownerDetails)}
      {Object.keys(detailsWithoutSubchildren).length > 0 && <DetailSection details={detailsWithoutSubchildren} />}
      {Object.keys(keysWithSubchildren).length > 0 && keysWithSubchildren.map(d => <DetailSection key={d} sectionLabel={d} details={details[d]} />)}
      {signature && <PIRAuthorization {...signature} onSuccess={onSignatureUploadSuccess} />}
    </SectionContainer>
  )
}

function renderRequestInformation(requestInformation) {
  const selectedTier = requestInformation?.['Selected Tier']
  const tierOptions = requestInformation?.tierOptions || []
  const additionalDetails = requestInformation?.['Additional Details']
  const hasTier = Boolean(selectedTier)
  const hasAdditionalDetails = Boolean(additionalDetails)

  if (!hasTier && !hasAdditionalDetails) return null

  return (
    <SectionContainer>
      <BoldBodyText>Request Information</BoldBodyText>
      <Divider />
      {hasTier && (
        <TierDetailsRow>
          <TierLabel>Selected Tier</TierLabel>
          <TierValueContainer>
            <TierName>{selectedTier}</TierName>
            {tierOptions.length > 0 && (
              <TierOptionsContainer>
                {tierOptions.map((option, index) => (
                  <TierOptionItem key={index}>{option}</TierOptionItem>
                ))}
              </TierOptionsContainer>
            )}
          </TierValueContainer>
        </TierDetailsRow>
      )}
      {hasAdditionalDetails && (
        <AdditionalDetailsContainer>
          <DetailSection details={{ 'Additional Details': additionalDetails }} />
        </AdditionalDetailsContainer>
      )}
    </SectionContainer>
  )
}

function RequestDetails({
  pirDetails = {},
  pir = {},
  pirSignatures = [],
  ownerDetails = {},
  onSignatureUploadSuccess = () => {},
  onRequestUpdated = () => {}
}) {
  const [editOpen, setEditOpen] = useState(false)
  const canEdit = EDITABLE_PIR_REQUEST_STATUSES.includes(pir.status)
  const sameOwner = pirDetails.requestorInformation.Email === pirDetails.ownerInformation.Email
  const sameStrata =
    pirDetails.strataInformation && pirDetails.requestorInformation.Email === pirDetails.strataInformation['Contact Information'].Email

  const sameString = pirTerminology.getSameContactPhrase({ sameOwner, sameStrata })

  const ownerSignature = pirSignatures.find(s => s.authorizerRole === 'OWNER')
  const requestorSignature = pirSignatures.find(s => s.authorizerRole === 'REQUESTOR')
  const strataSignature = pirDetails.strataInformation ? pirSignatures.find(s => s.authorizerRole === 'STRATA') : undefined

  return (
    <StyledFlexColumn>
      <SpaceBetweenRowContainer>
        <BoldFormTitle>Request Details</BoldFormTitle>
        {canEdit && (
          <SecondaryButton onClick={() => setEditOpen(true)}>
            <EditIcon /> Edit Details
          </SecondaryButton>
        )}
      </SpaceBetweenRowContainer>
      {(sameOwner || sameStrata) && <WarningMessage>The {sameString} and requestor on this request have the same email.</WarningMessage>}
      {renderDetails('Property Information', pirDetails.propertyInformation)}
      {pirDetails.requestInformation && renderRequestInformation(pirDetails.requestInformation)}
      {renderDetails('Owner Information', pirDetails.ownerInformation, ownerSignature, onSignatureUploadSuccess, ownerDetails)}
      {pirDetails.strataInformation &&
        renderDetails(pirTerminology.getAssociationLabel('Information'), pirDetails.strataInformation, strataSignature, onSignatureUploadSuccess)}
      {renderDetails('Requestor Information', pirDetails.requestorInformation, requestorSignature, onSignatureUploadSuccess)}
      {canEdit && <EditPirDetailsDialog open={editOpen} pir={pir} onClose={() => setEditOpen(false)} onSuccess={onRequestUpdated} />}
    </StyledFlexColumn>
  )
}

RequestDetails.propTypes = {
  pirDetails: PropTypes.shape({
    ownerInformation: PropTypes.object.isRequired,
    propertyInformation: PropTypes.object.isRequired,
    requestorInformation: PropTypes.object.isRequired,
    strataInformation: PropTypes.object
  }).isRequired,
  pir: PropTypes.object,
  ownerDetails: PropTypes.object,
  pirSignatures: PropTypes.array.isRequired,
  onSignatureUploadSuccess: PropTypes.func,
  onRequestUpdated: PropTypes.func
}
export default RequestDetails
